import * as React from 'react';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';

import cn from 'classnames';

import { ButtonModel } from '@notino/react-styleguide';

import { RequestState } from '@containers/App/model';
import { noop } from '@utils/noop';

import { messages } from './messages';
import { buyButtonWrappableCss, StyledBuyButton } from './styled';

interface IBuyButtonWithDefaultsProps {
  buttonStyle?: ButtonModel.Styles;
  buttonSize?: ButtonModel.Sizes;
  buttonElementId?: string;
  buttonElementClassName?: string;
  buttonType?: 'button' | 'submit';
  wrapable: boolean;
  buttonText?: MessageDescriptor;
  /** Appended to the visible text in the accessible name so the button is understandable out of context */
  productLabel?: string;
  sendState?: RequestState | null;
  onMouseOver?: () => void;
  onClick?: (e: React.SyntheticEvent<HTMLElement>) => void;
  disabled?: boolean;
}

const ERROR_STATE_ARRAY = [RequestState.FAILED, RequestState.RETRYING];
const LOADING_STATE_ARRAY = [RequestState.IN_PROGRESS, RequestState.RETRYING];

export const BuyButtonWithDefaults: React.FC<IBuyButtonWithDefaultsProps> =
  React.memo(
    ({
      buttonElementId,
      buttonElementClassName,
      wrapable,
      disabled,
      sendState = null,
      buttonStyle = ButtonModel.Styles.primary,
      buttonText = messages.addToCart,
      buttonSize = ButtonModel.Sizes.default,
      buttonType = 'button',
      productLabel,
      onMouseOver = noop,
      onClick = noop,
    }) => {
      const { formatMessage } = useIntl();
      const isLoading =
        sendState !== null && LOADING_STATE_ARRAY.includes(sendState);
      const isError =
        sendState !== null && ERROR_STATE_ARRAY.includes(sendState);

      const isDisabled = disabled || isLoading;

      const ariaLabel = isLoading
        ? formatMessage(messages.buyButtonLoading)
        : isError
        ? formatMessage(messages.buyButtonError)
        : productLabel
        ? `${formatMessage(buttonText)} - ${productLabel}`
        : undefined;

      return (
        <StyledBuyButton
          disabled={isDisabled}
          onMouseOver={onMouseOver}
          buttonStyle={buttonStyle}
          buttonSize={buttonSize}
          type={buttonType}
          onClick={onClick}
          isLoading={isLoading}
          error={isError}
          id={buttonElementId}
          className={cn(
            { [buyButtonWrappableCss]: wrapable },
            buttonElementClassName
          )}
          data-testid={buttonElementId}
          aria-busy={isLoading}
          aria-label={ariaLabel}
        >
          <FormattedMessage {...buttonText} />
        </StyledBuyButton>
      );
    }
  );
